import { useCallback, useEffect, useState } from "react";
import type { Panel, PanelLoadReport, PanelRow } from "@electro-planner/domain";
import { interpretComponentCode } from "@electro-planner/components";
import { useDomainMutations } from "./hooks/useDomainMutations.ts";
import { usePanelProjection } from "./hooks/usePanelProjection.ts";
import { useYjsDoc } from "./hooks/useYjsDoc.ts";
import { AbbreviationsDialog } from "./components/AbbreviationsDialog.tsx";
import { DinLayout } from "./components/DinLayout.tsx";
import { PanelTable } from "./components/PanelTable.tsx";
import { PanelToolbar } from "./components/PanelToolbar.tsx";
import { ProjectInfoDialog } from "./components/ProjectInfoDialog.tsx";
import { ReleaseNotesDialog } from "./components/ReleaseNotesDialog.tsx";
import { RevisionHistoryDialog } from "./components/RevisionHistoryDialog.tsx";
import { SldView } from "./components/SldView.tsx";
import { SplitPane } from "./components/SplitPane.tsx";
import { SymbolPickerDialog } from "./components/SymbolPickerDialog.tsx";
import { exportPanelToPdf } from "./export/pdf.ts";
import { exportPanelToXlsx } from "./export/xlsx.ts";
import { sharePanelFile } from "./export/share.ts";
import { LinkProjectDialog } from "./linkedProjects/LinkProjectDialog.tsx";
import {
  readLinkedProject,
  type LinkedProject,
} from "./linkedProjects/registry.ts";

type EditorTab = "table" | "layout" | "sld";

/**
 * Rotkomponent för ett öppet centralspec-dokument. Håller ihop Yjs-dok,
 * mutationer, projektion till React och UI (toolbar + tabell).
 */
export function PanelEditor({
  documentId,
  onSwitchDocument,
}: {
  documentId: string;
  onSwitchDocument: (id: string) => void;
}) {
  const { doc, maps, synced } = useYjsDoc(documentId);
  const projection = usePanelProjection(doc, maps);
  const mutations = useDomainMutations(doc, maps);
  const [showReleases, setShowReleases] = useState(false);
  const [tab, setTab] = useState<EditorTab>("table");
  const [linkOpen, setLinkOpen] = useState(false);
  const [abbrevOpen, setAbbrevOpen] = useState(false);
  const [projectInfoOpen, setProjectInfoOpen] = useState(false);
  const [revisionsOpen, setRevisionsOpen] = useState(false);
  const [symbolPickerOpen, setSymbolPickerOpen] = useState(false);
  const [linked, setLinked] = useState<LinkedProject | null>(null);

  useEffect(() => {
    setLinked(readLinkedProject(documentId));
  }, [documentId]);

  // Globala tangentbordsgenvägar som inte är cellbundna.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // Öppna förkortningar med ? (eller Shift+/).
      if (!e.ctrlKey && !e.metaKey && !e.altKey && e.key === "?") {
        const t = e.target as HTMLElement | null;
        if (t?.tagName === "INPUT" || t?.tagName === "TEXTAREA") return;
        e.preventDefault();
        setAbbrevOpen(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const onTitleChange = useCallback(
    (v: string) => mutations.patchPanelMeta({ title: v }),
    [mutations],
  );
  const onDesignationChange = useCallback(
    (v: string) => mutations.patchPanelMeta({ designation: v }),
    [mutations],
  );
  const onBusbarChange = useCallback(
    (v: number | undefined) => mutations.patchPanelMeta({ busbarAmps: v }),
    [mutations],
  );
  const onEarthingChange = useCallback(
    (v: Panel["earthingSystem"]) => mutations.patchPanelMeta({ earthingSystem: v }),
    [mutations],
  );
  const onStatusChange = useCallback(
    (v: Panel["status"]) => mutations.patchPanelMeta({ status: v }),
    [mutations],
  );

  const onExportPdf = useCallback(() => {
    exportPanelToPdf({
      panel: projection.panel,
      sections: projection.sections,
      rows: projection.rows,
      layout: projection.layout,
    });
  }, [projection]);

  const onExportXlsx = useCallback(() => {
    void exportPanelToXlsx({
      panel: projection.panel,
      sections: projection.sections,
      rows: projection.rows,
    });
  }, [projection]);

  const onShare = useCallback(() => {
    sharePanelFile({
      panel: projection.panel,
      sections: projection.sections,
      rows: projection.rows,
    });
  }, [projection]);

  if (!synced) {
    return (
      <div className="flex h-full w-full items-center justify-center text-slate-500">
        Laddar…
      </div>
    );
  }

  return (
    <div className="flex h-full w-full flex-col bg-slate-50">
      <PanelToolbar
        documentId={documentId}
        panel={projection.panel}
        tab={tab}
        linkedProject={linked}
        onTabChange={setTab}
        onTitleChange={onTitleChange}
        onDesignationChange={onDesignationChange}
        onBusbarChange={onBusbarChange}
        onEarthingChange={onEarthingChange}
        onStatusChange={onStatusChange}
        onSwitchDocument={onSwitchDocument}
        onOpenRelease={() => setShowReleases(true)}
        onOpenLink={() => setLinkOpen(true)}
        onOpenAbbrev={() => setAbbrevOpen(true)}
        onOpenProjectInfo={() => setProjectInfoOpen(true)}
        onOpenRevisions={() => setRevisionsOpen(true)}
        onOpenSymbolPicker={() => setSymbolPickerOpen(true)}
        onShare={onShare}
        onExportPdf={onExportPdf}
        onExportXlsx={onExportXlsx}
      />

      <div className="flex min-h-0 flex-1 flex-col">
        {tab === "table" ? (
          <SplitPane
            storageKey="electro-planner-panels:splitpane:table"
            defaultLeftFraction={0.35}
            minLeftFraction={0.2}
            maxLeftFraction={0.6}
            left={
              <SldView
                panel={projection.panel}
                sections={projection.sections}
                rows={projection.rows}
              />
            }
            right={
              <PanelTable
                rows={projection.rows}
                sections={projection.sections}
                warnings={projection.warnings}
                load={projection.load}
                mutations={mutations}
                linkedProject={linked}
              />
            }
          />
        ) : tab === "layout" ? (
          <DinLayout
            panel={projection.panel}
            layout={projection.layout}
            mutations={mutations}
          />
        ) : (
          <SldView
            panel={projection.panel}
            sections={projection.sections}
            rows={projection.rows}
          />
        )}
      </div>

      <StatusBar
        warnings={projection.warnings.length}
        load={projection.load}
        busbarAmps={projection.panel?.busbarAmps}
      />

      {showReleases ? (
        <ReleaseNotesDialog
          forceOpen
          onClose={() => setShowReleases(false)}
        />
      ) : (
        <ReleaseNotesDialog />
      )}

      {linkOpen ? (
        <LinkProjectDialog
          documentId={documentId}
          existing={linked}
          onClose={() => setLinkOpen(false)}
          onLinked={setLinked}
        />
      ) : null}

      <AbbreviationsDialog open={abbrevOpen} onClose={() => setAbbrevOpen(false)} />
      <ProjectInfoDialog
        open={projectInfoOpen}
        panel={projection.panel}
        onClose={() => setProjectInfoOpen(false)}
        onChange={(patch) => mutations.patchPanelMeta(patch)}
      />
      <RevisionHistoryDialog
        open={revisionsOpen}
        panel={projection.panel}
        onClose={() => setRevisionsOpen(false)}
        onTakeSnapshot={(label, note) =>
          mutations.takeRevisionSnapshot(label, note)
        }
      />
      <SymbolPickerDialog
        open={symbolPickerOpen}
        onClose={() => setSymbolPickerOpen(false)}
        onInsert={(shortCode) => {
          // Kör kortkoden genom tolkaren så A/poler/karakteristik/etc. fylls i
          // automatiskt. Matchar inte koden fyller vi bara i component-fältet.
          const interpreted = interpretComponentCode(shortCode);
          const patch: Partial<PanelRow> = interpreted
            ? interpreted.patch
            : { component: shortCode };
          mutations.addRow(patch);
        }}
      />
    </div>
  );
}

/**
 * Statusrad längst ner: panelens totala last + ev. varning mot busbar-ström,
 * samt räkning av valideringsvarningar. Göms i utskrift.
 */
function StatusBar({
  warnings,
  load,
  busbarAmps,
}: {
  warnings: number;
  load: PanelLoadReport;
  busbarAmps?: number;
}) {
  const amps = load.panel.amps;
  const pct = busbarAmps ? (amps / busbarAmps) * 100 : undefined;
  const overBusbar = load.overBusbar;

  return (
    <div className="no-print flex flex-wrap items-center gap-4 border-t border-slate-200 bg-white px-3 py-1 text-xs">
      <span className="text-slate-600">
        <strong>{load.panel.rowCount}</strong> rader · totalt{" "}
        <strong>{Math.round(load.panel.watts)} W</strong> ·{" "}
        <strong
          className={overBusbar ? "text-rose-600" : ""}
        >
          {amps.toFixed(1)} A
        </strong>
        {busbarAmps ? (
          <>
            {" "}/ {busbarAmps} A
            {pct !== undefined ? ` (${Math.round(pct)}%)` : null}
          </>
        ) : null}
      </span>
      {overBusbar ? (
        <span className="font-medium text-rose-700">
          ⚠ Total last överstiger huvudskenans kapacitet
        </span>
      ) : null}
      {warnings > 0 ? (
        <span className="ml-auto text-amber-800">
          {warnings} varning{warnings === 1 ? "" : "ar"} — orangemarkerade celler
        </span>
      ) : (
        <span className="ml-auto text-slate-400">Inga varningar</span>
      )}
    </div>
  );
}
